import {Action, ReducerBuilder} from "redux-ts";
import {Reducer} from "platform/redux/Reducer";
import Utils from "platform/util/Utils";
import {OnAccountChanged, OnLoggedOut} from "core/redux/auth/AuthReduxActions";
import {InboxMessage, InboxReduxState, PushNotification} from "core/redux/inbox/InboxReduxState";
import {
    AddInboxMessage,
    AddInboxMessagePayload,
    RemoveInboxMessage,
    SetPushNotification,
    SetPushNotificationPayload,
    RemoveInboxMessagePayload,
    ResetInboxUnreadCount,
    RemovePushNotification,
    SetNotificationTab,
    SetNotificationTabPayload,
    FetchInboxMessagesType,
    SetInboxMessages,
    SetInboxMessagesPayload,
} from "core/redux/inbox/InboxReduxActions";
import {Increment} from "platform/util/Increment";
import {TSMap} from "typescript-map";
import Platform from "platform/Platform";
import {NotificationTab} from "enum/NotificationTab";
import InboxEngine from "core/engine/InboxEngine";
import {ServiceType} from "enum/ServiceType";

const initialState = (): InboxReduxState => {

    return {
        messages: [],
        all: [],
        unseen: [],
        unreadCount: 0,
        notification: null,
        notificationTab: NotificationTab.All
    };
};

export default class InboxReducer extends Reducer<InboxReduxState> {

    private static _instance: InboxReducer;
    private _increment: Increment = Increment.from(0);
    private _timeouts: TSMap<number, any> = new TSMap();

    public static instance(): InboxReducer {
        return this._instance || (this._instance = new this());
    }

    private constructor() {
        super();
        const inboxEngine: InboxEngine = Platform.engine(ServiceType.Inbox);
        this._middlewareActions.set(FetchInboxMessagesType, inboxEngine.doFetchInboxMessages);
    }

    public get name(): string {
        return "inbox";
    }

    protected setup(builder: ReducerBuilder<InboxReduxState>): void {
        builder
            .init(initialState())
            .handle(OnLoggedOut, (state: InboxReduxState, action: Action<any>) => {
                return {
                    ...initialState()
                };
            })
            .handle(OnAccountChanged, (state: InboxReduxState, action: Action<any>) => {
                return {
                    ...initialState(),
                    all: state.all
                };
            })
            .handle(SetNotificationTab, (state: InboxReduxState, {payload}: Action<SetNotificationTabPayload>) => {
                return Object.assign({}, state, {
                    notificationTab: payload.tab
                });
            })
            .handle(SetPushNotification, (state: InboxReduxState, {payload}: Action<SetPushNotificationPayload>) => {
                const newState: InboxReduxState = Utils.merge({}, state);
                const notification: PushNotification = payload.message;
                newState.notification = notification;
                return newState;
            })
            .handle(RemovePushNotification, (state: InboxReduxState, action: Action<any>) => {
                const newState: InboxReduxState = Utils.merge({}, state);
                newState.notification = null;
                return newState;

            })
            .handle(SetInboxMessages, (state: InboxReduxState, {payload}: Action<SetInboxMessagesPayload>) => {
                return Object.assign({}, state, {
                    all: payload.messages
                });
            })
            .handle(AddInboxMessage, (state: InboxReduxState, {payload}: Action<AddInboxMessagePayload>) => {
                this._logger.debug("Add inbox message. Type: " + payload.message.type);
                const newState: InboxReduxState = Utils.merge({}, state);
                const id: number = this._increment.next();
                const date: Date = new Date();
                const message: InboxMessage = {
                    ...payload.message,
                    id,
                    date
                };
                newState.messages = [message, ...state.messages];
                newState.all = [message, ...state.all];
                newState.unreadCount = state.unreadCount + 1;
                this._timeouts.set(id, setTimeout(() => {
                    this._timeouts.delete(id);
                    Platform.dispatch(RemoveInboxMessage({message}));
                }, 5000));
                return newState;
            })
            .handle(RemoveInboxMessage, (state: InboxReduxState, {payload}: Action<RemoveInboxMessagePayload>) => {
                const id: number = payload.message.id;
                const timeout: any = this._timeouts.get(id);
                if (timeout) {
                    clearTimeout(timeout);
                    this._timeouts.delete(id);
                }
                const newState: InboxReduxState = Utils.merge({}, state);
                newState.messages = state.messages.filter((message: InboxMessage) => message.id !== id);
                return newState;
            })
            .handle(ResetInboxUnreadCount, (state: InboxReduxState, action: Action<any>) => {
                return Object.assign({}, state, {
                    unreadCount: 0
                });
            });
    }
}
